import { useState, useCallback } from 'react';

import PrimarySettings from '@/features/task-configuration/components/PrimarySettings';
import TaskFilterListTable from '@/features/task-configuration/components/TaskFilterListTable';
import { useGetFilterListQuery } from '@/features/task-configuration/api/taskConfigurationApi';
import LoaderComponent from '@/components/LoaderComponent';
import FilterConfigurationTopBar from '@/features/task-configuration/components/FilterConfigurationTopBar';

const FilterConfiguration = ({ openPrimarySettings, setOpenPrimarySettings, editFilterId }) => {
	const [selectedRowToEdit, setSelectedRowToEdit] = useState(
		editFilterId ? { id: Number(editFilterId) } : null
	);

	// When opened via URL param (new tab), closing should close the tab
	const handleCloseSettings = useCallback(
		(value) => {
			if (!value && editFilterId) {
				window.close();
			} else {
				setOpenPrimarySettings(value);
			}
		},
		[editFilterId, setOpenPrimarySettings]
	);
	const [listSearchKey, setListSearchKey] = useState('');
	const { data: filterList = [], isLoading: filterListLoading } = useGetFilterListQuery();

	// Filter data based on search key
	const filteredList = filterList.filter((item) => {
		if (!listSearchKey.trim()) return true;

		const searchLower = listSearchKey.toLowerCase();
		const title = item?.title?.toLowerCase() || '';
		const details = item?.details?.toLowerCase() || '';
		const categoryName = item?.category?.name?.toLowerCase() || '';

		return (
			title.includes(searchLower) || details.includes(searchLower) || categoryName.includes(searchLower)
		);
	});

	// Group filtered data by category
	const groupedByCategory = filteredList.reduce((acc, item) => {
		const categoryId = item?.category?.id || 'uncategorized';
		const categoryName = item?.category?.name || 'Uncategorized';
		if (!acc[categoryId]) {
			acc[categoryId] = {
				id: categoryId,
				name: categoryName,
				items: []
			};
		}
		acc[categoryId].items.push(item);
		return acc;
	}, {});

	// Sort grouped categories by category.id
	const sortedCategories = Object.entries(groupedByCategory).sort(([idA], [idB]) => {
		// Handle uncategorized items (put them at the end)
		if (idA === 'uncategorized') return 1;
		if (idB === 'uncategorized') return -1;
		return Number(idA) - Number(idB);
	});

	const totalCategoryItem = sortedCategories.reduce((acc, [, category]) => acc + category.items.length, 0);

	return (
		<LoaderComponent loading={filterListLoading}>
			<div>
				{openPrimarySettings ? (
					<PrimarySettings
						setOpenPrimarySettings={handleCloseSettings}
						selectedRowToEdit={selectedRowToEdit}
						setSelectedRowToEdit={setSelectedRowToEdit}
					/>
				) : (
					<>
						<FilterConfigurationTopBar
							setOpenPrimarySettings={setOpenPrimarySettings}
							searchKey={listSearchKey}
							onSearch={setListSearchKey}
							totalCategoryItem={totalCategoryItem}
						/>
						{sortedCategories.map(([categoryId, category]) => (
							<TaskFilterListTable
								key={categoryId}
								categoryName={category.name}
								items={category.items}
								setOpenPrimarySettings={setOpenPrimarySettings}
								setSelectedRowToEdit={setSelectedRowToEdit}
							/>
						))}
						{sortedCategories.length === 0 && (
							<div className="journey-configuration-container py-2">
								<div className="text-center py-5">
									<p className="text-muted">No data available</p>
								</div>
							</div>
						)}
					</>
				)}
			</div>
		</LoaderComponent>
	);
};

export default FilterConfiguration;
